import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar, Box, Toolbar, Button, Stack, Drawer,
  IconButton, List, ListItem, ListItemButton, ListItemText,
  Divider, Typography, Tooltip
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import { useWeb3React } from '@web3-react/core';

import AccountDialog from './components/accountDialog/AccountDialog';
import { useThemeMode } from '../../context/ThemeContext';

import LogoWhite from '../../resources/logo_white.svg';
import LogoDark  from '../../resources/logo_dark.svg';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showDialog, setShowDialog] = useState(false);

  const { account } = useWeb3React();
  const navigate   = useNavigate();
  const location   = useLocation();
  const theme      = useTheme();
  const { mode, toggleMode } = useThemeMode();

  const isDark = mode === 'dark';

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const pages = [
    { name: 'Home',         link: '/' },
    { name: 'Dashboard',    link: '/allocation-staking' },
    { name: 'Private Sale', link: '/sales' },
    { name: 'Swap',         link: '/swap' },
  ];

  const handleNavClick = (path) => {
    navigate(path);
    setMobileOpen(false);
  };

  // Derived colors
  const scrolledBg = isDark
    ? 'rgba(5,9,15,0.88)'
    : 'rgba(255,255,255,0.92)';
  const pillBg = isDark
    ? (isScrolled ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.2)')
    : (isScrolled ? 'rgba(10,16,32,0.05)'    : 'rgba(255,255,255,0.7)');
  const pillBorder = isDark
    ? 'rgba(255,255,255,0.06)'
    : 'rgba(10,16,32,0.1)';
  const inactiveText = isDark ? '#94a3b8' : '#5c6b7f';

  return (
    <>
      <AppBar
        position="fixed"
        sx={{
          zIndex: 1200,
          bgcolor: isScrolled ? scrolledBg : 'transparent',
          backdropFilter: isScrolled ? 'blur(14px)' : 'none',
          borderBottom: isScrolled
            ? `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : 'rgba(10,16,32,0.09)'}`
            : '1px solid transparent',
          transition: 'all 0.3s cubic-bezier(0.4,0,0.2,1)',
          py: isScrolled ? 0.5 : 2,
        }}
      >
        <Box maxWidth="xl" sx={{ mx: 'auto', width: '100%', px: { xs: 2, md: 6 } }}>
          <Toolbar disableGutters sx={{ justifyContent: 'space-between', minHeight: { xs: 64, md: 72 } }}>

            {/* Logo */}
            <Box
              component="img"
              src={isDark ? LogoWhite : LogoDark}
              alt="eBonds"
              sx={{
                height: { xs: 30, md: 40 },
                cursor: 'pointer',
                transition: 'transform 0.2s',
                '&:hover': { transform: 'scale(1.04)' },
              }}
              onClick={() => navigate('/')}
            />

            {/* Desktop nav pills */}
            <Stack
              direction="row"
              spacing={0.5}
              sx={{
                display: { xs: 'none', md: 'flex' },
                bgcolor: pillBg,
                p: 0.75,
                borderRadius: 10,
                border: `1px solid ${pillBorder}`,
                backdropFilter: 'blur(4px)',
              }}
            >
              {pages.map((page) => {
                const isActive = location.pathname === page.link;
                return (
                  <Button
                    key={page.name}
                    onClick={() => navigate(page.link)}
                    disableRipple
                    sx={{
                      color: isActive ? (isDark ? '#000' : '#fff') : inactiveText,
                      bgcolor: isActive ? theme.palette.primary.main : 'transparent',
                      fontSize: '0.85rem',
                      fontWeight: isActive ? 700 : 500,
                      px: 2.5, py: 0.75,
                      borderRadius: 8,
                      minWidth: 'auto',
                      transition: 'all 0.18s ease',
                      '&:hover': {
                        color: isActive ? (isDark ? '#000' : '#fff') : (isDark ? '#fff' : '#0a1020'),
                        bgcolor: isActive
                          ? theme.palette.primary.main
                          : isDark ? 'rgba(255,255,255,0.08)' : 'rgba(10,16,32,0.06)',
                      },
                    }}
                  >
                    {page.name}
                  </Button>
                );
              })}
            </Stack>

            {/* Right actions */}
            <Stack direction="row" spacing={1} alignItems="center">
              {/* Theme toggle */}
              <Tooltip title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}>
                <IconButton
                  onClick={toggleMode}
                  size="small"
                  sx={{
                    border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(10,16,32,0.12)'}`,
                    borderRadius: 3,
                    color: isDark ? '#94a3b8' : '#5c6b7f',
                    width: 38, height: 38,
                    '&:hover': {
                      color: theme.palette.primary.main,
                      borderColor: theme.palette.primary.main,
                      bgcolor: isDark ? 'rgba(210,157,92,0.08)' : 'rgba(200,144,74,0.06)',
                    },
                    transition: 'all 0.18s ease',
                  }}
                >
                  {isDark ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
                </IconButton>
              </Tooltip>

              {/* Wallet */}
              {!account ? (
                <Button
                  variant="contained"
                  onClick={() => setShowDialog(true)}
                  sx={{
                    fontWeight: 700, px: 3, borderRadius: 8,
                    boxShadow: `0 4px 14px ${isDark ? 'rgba(210,157,92,0.3)' : 'rgba(200,144,74,0.25)'}`,
                    '&:hover': { boxShadow: `0 6px 20px ${isDark ? 'rgba(210,157,92,0.5)' : 'rgba(200,144,74,0.4)'}` },
                  }}
                >
                  Connect
                </Button>
              ) : (
                <Button
                  variant="outlined"
                  startIcon={<AccountBalanceWalletIcon />}
                  onClick={() => setShowDialog(true)}
                  sx={{
                    borderRadius: 8, px: 2.5,
                    borderColor: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(10,16,32,0.15)',
                    color: isDark ? 'white' : '#0a1020',
                  }}
                >
                  {account.substring(0, 6)}...
                </Button>
              )}

              {/* Mobile menu button */}
              <IconButton
                onClick={() => setMobileOpen(!mobileOpen)}
                sx={{
                  display: { md: 'none' },
                  border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(10,16,32,0.12)'}`,
                  borderRadius: 3,
                  color: isDark ? 'white' : '#0a1020',
                  bgcolor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(10,16,32,0.03)',
                }}
              >
                <MenuIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Toolbar>
        </Box>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        PaperProps={{
          sx: {
            width: '100%', maxWidth: 320,
            bgcolor: 'background.default',
            borderLeft: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(10,16,32,0.08)'}`,
          },
        }}
      >
        <Box sx={{ p: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box component="img" src={isDark ? LogoWhite : LogoDark} alt="eBonds" sx={{ height: 30 }} />
          <Stack direction="row" spacing={1}>
            <IconButton
              onClick={toggleMode}
              size="small"
              sx={{ color: 'text.secondary', border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(10,16,32,0.1)'}`, borderRadius: 2 }}
            >
              {isDark ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
            </IconButton>
            <IconButton
              onClick={() => setMobileOpen(false)}
              sx={{ color: 'text.secondary', border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(10,16,32,0.1)'}`, borderRadius: 2 }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Box>

        <Divider />

        <List sx={{ px: 2, pt: 3 }}>
          {pages.map((page) => {
            const isActive = location.pathname === page.link;
            return (
              <ListItem key={page.name} disablePadding sx={{ mb: 1 }}>
                <ListItemButton
                  onClick={() => handleNavClick(page.link)}
                  sx={{
                    borderRadius: 3, py: 1.5,
                    bgcolor: isActive
                      ? isDark ? 'rgba(210,157,92,0.08)' : 'rgba(200,144,74,0.06)'
                      : 'transparent',
                    border: isActive
                      ? `1px solid ${isDark ? 'rgba(210,157,92,0.15)' : 'rgba(200,144,74,0.2)'}`
                      : '1px solid transparent',
                    '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(10,16,32,0.03)' },
                  }}
                >
                  <ListItemText
                    primary={page.name}
                    primaryTypographyProps={{
                      fontWeight: isActive ? 700 : 500,
                      color: isActive ? 'primary.main' : 'text.primary',
                      fontSize: '1rem',
                    }}
                  />
                  {isActive && <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'primary.main' }} />}
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>

        <Box sx={{ mt: 'auto', p: 4 }}>
          <Button
            fullWidth variant="contained" size="large"
            onClick={() => { setShowDialog(true); setMobileOpen(false); }}
            sx={{ py: 2, borderRadius: 4 }}
          >
            {account ? 'Manage Wallet' : 'Connect Wallet'}
          </Button>
          <Box sx={{ textAlign: 'center', mt: 2 }}>
            <Typography variant="caption" color="text.secondary">V 0.1.0 Beta</Typography>
          </Box>
        </Box>
      </Drawer>

      <AccountDialog open={showDialog} setOpen={setShowDialog} />
    </>
  );
};

export default Header;
